// The gateway's socket.io, as onlycat-admin's OCGatewayClient: websocket
// transport, auth {token: deviceToken}. Only sign-in (loginWithAuth0) and
// logout use it, so it connects lazily and fixture mode never touches it.
import type { Socket } from "socket.io-client";
import { getDeviceToken } from "./platform";

const GATEWAY = import.meta.env.VITE_GATEWAY_URL || "https://gateway.onlycat.com";

let socket: Socket | null = null;

async function connect(): Promise<Socket> {
  if (socket) return socket;
  const { io } = await import("socket.io-client");
  socket = io(GATEWAY, {
    transports: ["websocket"],
    query: { platform: "web", device: "production-planner" },
    auth: (cb) => cb({ token: getDeviceToken() })
  });
  return socket;
}

/** One request/acknowledge round trip; rejects on a non-200 code or a disconnect. */
export async function gatewayRequest<T = unknown>(event: string, payload: unknown, timeoutMs = 30_000): Promise<T> {
  const s = await connect();
  return new Promise<T>((resolve, reject) => {
    const onDisconnect = () => reject({ code: 1006, message: "Disconnected" });
    const timer = setTimeout(() => { s.off("disconnect", onDisconnect); reject({ code: 408, message: "The gateway didn't answer" }); }, timeoutMs);
    s.once("disconnect", onDisconnect);
    s.emit(event, payload, (response: { code?: number } & T) => {
      clearTimeout(timer);
      s.off("disconnect", onDisconnect);
      if (response && typeof response === "object" && response.code && response.code !== 200) reject(response);
      else resolve(response);
    });
  });
}

export function disconnectGateway(): void {
  socket?.disconnect();
  socket = null;
}
